import { useEffect, useState } from 'react';
import { sendGrowthOutreach, type GrowthPerson } from '@/lib/admin-data';
import { INBOUND_CHANNEL_LABEL, messagePerson, replySubject, type InboundChannel, type MessagePersonResult } from '@/lib/people-data';
import { when } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * One personal note to one person. The suggested draft (written server side
 * for their exact state) is the starting point; Brian's edits live in this
 * browser until the note is sent, so a refresh or a redeploy loses nothing.
 *
 * Two roads out, one composer (pv-auth #332 said "extend it, do not
 * duplicate it"):
 *   via 'growth'  the Growth page's outreach endpoint, as before: sent,
 *                 queued (GHL busy, it sends itself) or failed, each said plainly.
 *   via 'people'  POST /provider/admin/people/:id/message, the person page's
 *                 real send, which answers with `deliverySummary`, the one
 *                 sentence that says whether the person got it. With `replyTo`
 *                 it answers one of their inbound messages (#336): the send
 *                 carries replyToMessageId, an email goes under "Re: theirs",
 *                 and a send that went out marks their message read.
 *
 * A reply keeps its own draft key, so a half-written reply to one message
 * never overwrites a note to the same person. Give the element a `key` that
 * changes with the reply target, so the fields reset when Brian picks a
 * different message to answer.
 */
export interface ComposerDraft { channel: 'email' | 'sms'; subject: string; body: string }
export type ComposerTarget = Pick<GrowthPerson, 'userId' | 'phone'> & { draft: ComposerDraft };
export interface ReplyTarget { messageId: string; channel: InboundChannel; subject: string | null; receivedAt: string }
export const EMPTY_DRAFT: ComposerDraft = { channel: 'email', subject: '', body: '' };

export function OutreachComposer({ p, onSent, rows = 7, via = 'growth', replyTo = null, onClose }: {
  p: ComposerTarget;
  onSent: (result?: MessagePersonResult) => void;
  rows?: number;
  via?: 'growth' | 'people';
  replyTo?: ReplyTarget | null;
  onClose?: () => void;
}) {
  // A reply starts on their channel (SMS only with a phone on file) and, by email, under their subject.
  const base: ComposerDraft = replyTo
    ? { channel: replyTo.channel === 'sms' && p.phone ? 'sms' : 'email', subject: p.draft.subject || replySubject(replyTo.subject), body: p.draft.body }
    : p.draft;
  const [channel, setChannel] = useState<'email' | 'sms'>(base.channel);
  const draftKey = via === 'people' ? `pv_people_draft_${p.userId}_${replyTo?.messageId ?? 'new'}` : `pv_growth_draft_${p.userId}`;
  const saved = (() => { try { return JSON.parse(localStorage.getItem(draftKey) || 'null'); } catch { return null; } })() as { subject: string; body: string } | null;
  const [subject, setSubject] = useState(saved?.subject ?? base.subject);
  const [body, setBody] = useState(saved?.body ?? base.body);
  useEffect(() => {
    try {
      if (body === base.body && subject === base.subject) localStorage.removeItem(draftKey);
      else localStorage.setItem(draftKey, JSON.stringify({ subject, body }));
    } catch { /* storage unavailable: the note still lives in the textarea */ }
  }, [body, subject, draftKey, base.body, base.subject]);
  const useSuggested = () => { setSubject(base.subject); setBody(base.body); };
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      if (via === 'people') {
        const r = await messagePerson({ userId: p.userId, channel, subject: channel === 'email' ? subject : undefined, body, replyToMessageId: replyTo?.messageId ?? null });
        const extra = [r.status === 'queued' && r.message ? r.message : '', r.error ? r.error : '', replyTo && r.replyTo?.markedRead ? 'Their message is marked read.' : ''].filter(Boolean).join(' ');
        setResult(`${r.deliverySummary}${extra ? ` ${extra}` : ''}`);
        if (r.sentToPerson) {
          try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
          onSent(r);
        }
      } else {
        const r = await sendGrowthOutreach({ userId: p.userId, channel, subject: channel === 'email' ? subject : undefined, body });
        setResult(
          r.status === 'sent'
            ? `Sent by ${channel}${r.via === 'mailer' ? ' (through our mailer; GHL was busy)' : ''}.`
            : r.status === 'queued'
              ? (r.message ?? 'Queued. It sends itself when GHL answers.')
              : `Not sent: ${r.error ?? 'unknown error'}`,
        );
        if (r.status === 'sent' || r.status === 'queued') {
          try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
          onSent();
        }
      }
    } catch (err) {
      setResult(`Not sent: ${(err as Error).message}`);
    } finally {
      setSending(false);
    }
  };

  const edited = body !== base.body || subject !== base.subject;
  const title = replyTo
    ? `Reply to their ${INBOUND_CHANNEL_LABEL[replyTo.channel].toLowerCase()} of ${when(replyTo.receivedAt)}`
    : via === 'people' ? 'Send a message' : 'Personal note';
  return (
    <div className={s.draft} data-panel={via === 'people' ? 'message' : 'outreach'} data-reply-to={replyTo?.messageId}>
      <div className={s.draftHead}>
        <strong>{title}</strong>
        <span className={s.draftHeadRight}>
          <label className={s.sub}>
            via{' '}
            <select value={channel} onChange={(e) => setChannel(e.target.value as 'email' | 'sms')} aria-label="Channel">
              <option value="email">email</option>
              {p.phone && <option value="sms">SMS</option>}
            </select>
            {!p.phone && ' (no phone on file, so no SMS)'}
          </label>
          {onClose && <button type="button" className={s.linkBtn} onClick={onClose}>Close</button>}
        </span>
      </div>
      {replyTo && replyTo.subject && <div className={s.sub}>Answering: {replyTo.subject}</div>}
      {channel === 'email' && <input className={s.draftSubject} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" aria-label="Subject" />}
      <textarea className={s.draftBody} value={body} onChange={(e) => setBody(e.target.value)} rows={rows} aria-label="Message" placeholder={via === 'people' ? 'In your words. This goes to the person.' : undefined} />
      <div className={s.draftActions}>
        <button type="button" className={s.btn} disabled={sending || !body.trim()} onClick={send}>
          {sending ? 'Sending' : via === 'people' ? `Send a message by ${channel === 'sms' ? 'SMS' : 'email'}` : `Send by ${channel === 'sms' ? 'SMS' : 'email'}`}
        </button>
        {edited && base.body && <button type="button" className={s.linkBtn} onClick={useSuggested}>Use suggested text</button>}
        {result && <span className={s.sub} data-result>{result}</span>}
      </div>
      {via === 'people' && !result && <div className={s.sub}>Goes to the person through GHL, by {channel === 'sms' ? 'SMS' : 'email'}. The journal keeps a copy.</div>}
    </div>
  );
}
